/**
 * Generic wrapper functions for unified endpoint calls
 * Eliminates repetitive serverFetch/browserFetch implementations across v3 endpoints
 */

/**
 * Server-side fetch wrapper using Nuxt's useFetch
 * @template ParamType - Type of request parameters/body
 * @template ResponseType - Type of API response data
 * @param endpoint - The endpoint path (e.g., "/blog/posts/list")
 * @param params - Optional request parameters to send in body
 * @returns Promise with ApiServerResponse containing typed response data
 */
export const serverFetchWrapper = async <ParamType, ResponseType>(
  endpoint: string,
  params?: ParamType,
): Promise<ApiServerResponse<ResponseType>> => {
  const runtimeConfig = useRuntimeConfig()
  const url = `${runtimeConfig.public.NUXT_API_CONTENT_ROOT_URL}/rurality/v3${endpoint}`

  const { data } = await useFetch(url, {
    body: params ? { ...params } : undefined,
    method: 'POST',
    key: url + (params ? JSON.stringify(params) : ''),
  })

  return data.value as ApiServerResponse<ResponseType>
}

/**
 * Browser-side fetch wrapper using $fetch
 * @template ParamType - Type of request parameters/body
 * @template ResponseType - Type of API response data
 * @param endpoint - The endpoint path (e.g., "/blog/posts/list")
 * @param params - Optional request parameters to send in body
 * @returns Promise with ApiServerResponse containing typed response data
 */
export const browserFetchWrapper = async <ParamType, ResponseType>(
  endpoint: string,
  params?: ParamType,
): Promise<ApiServerResponse<ResponseType>> => {
  const runtimeConfig = useRuntimeConfig()
  const url = `${runtimeConfig.public.NUXT_API_CONTENT_ROOT_URL}/rurality/v3${endpoint}`

  const data = await $fetch(url, {
    body: params ? { ...params } : undefined,
    method: 'POST',
  })

  return data as ApiServerResponse<ResponseType>
}

/**
 * Server-side fetch wrapper for endpoints returning ResponseRuralityMetaType
 * Includes pagination and metadata in response
 * @template ParamType - Type of request parameters/body
 * @template ResponseType - Type of API response data
 * @param endpoint - The endpoint path (e.g., "/search/seo/destination")
 * @param params - Optional request parameters to send in body
 * @returns Promise with ResponseRuralityMetaType containing typed response data and metadata
 */
export const serverFetchWrapperMeta = async <ParamType, ResponseType>(
  endpoint: string,
  params?: ParamType,
): Promise<ResponseRuralityMetaType<ResponseType>> => {
  const runtimeConfig = useRuntimeConfig()
  const url = `${runtimeConfig.public.NUXT_API_CONTENT_ROOT_URL}/rurality/v3${endpoint}`

  const { data } = await useFetch(url, {
    body: params ? { ...params } : undefined,
    method: 'POST',
    key: url,
  })

  return data.value as ResponseRuralityMetaType<ResponseType>
}

/**
 * Browser-side fetch wrapper for endpoints returning ResponseRuralityMetaType
 * Includes pagination and metadata in response
 * @template ParamType - Type of request parameters/body
 * @template ResponseType - Type of API response data
 * @param endpoint - The endpoint path (e.g., "/search/seo/destination")
 * @param params - Optional request parameters to send in body
 * @returns Promise with ResponseRuralityMetaType containing typed response data and metadata
 */
export const browserFetchWrapperMeta = async <ParamType, ResponseType>(
  endpoint: string,
  params?: ParamType,
): Promise<ResponseRuralityMetaType<ResponseType>> => {
  const runtimeConfig = useRuntimeConfig()
  const url = `${runtimeConfig.public.NUXT_API_CONTENT_ROOT_URL}/rurality/v3${endpoint}`

  const data = await $fetch(url, {
    body: params ? { ...params } : undefined,
    method: 'POST',
  })

  return data as ResponseRuralityMetaType<ResponseType>
}
